import { useState } from "react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import { ArrowRight, Briefcase, HandHeart, Upload, Image as ImageIcon, X } from "lucide-react";

const AVAIL_OPTIONS = [
  "Friday Setup (Dec 9)",
  "Saturday Day-of (Dec 10)",
  "Saturday Evening",
  "Sunday Tear-down (Dec 11)",
];

const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const MAX_LOGO_BYTES = 5 * 1024 * 1024;

export function VendorForm() {
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    product_category: "", website: "", booth_preference: "", description: "",
    logo_url: "", logo_path: "", logo_original_filename: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState("");

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError("");

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setLogoError("Only PNG, JPG, JPEG, or WebP files are accepted.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError("Logo exceeds the 5 MB limit.");
      e.target.value = "";
      return;
    }

    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/vendors/logo", fd);
      setForm((prev) => ({
        ...prev,
        logo_url: r.data.logo_url,
        logo_path: r.data.logo_path,
        logo_original_filename: r.data.logo_original_filename || file.name,
      }));
      toast.success("Logo uploaded. It will remain private until an administrator approves it.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not upload logo.";
      setLogoError(msg);
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  const removeLogo = () => {
    setForm((prev) => ({ ...prev, logo_url: "", logo_path: "", logo_original_filename: "" }));
    setLogoError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/vendors", form);
      toast.success("Vendor application received. We'll review within 5 business days.");
      setForm({
        business_name: "", contact_name: "", email: "", phone: "",
        product_category: "", website: "", booth_preference: "", description: "",
        logo_url: "", logo_path: "", logo_original_filename: "",
      });
    } catch {
      toast.error("Could not submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="vendors"
      data-testid="vendor-section"
      className="relative py-24 md:py-32 scroll-mt-nav"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Briefcase className="text-aawta-pink" size={28} />
          <p className="text-overline text-aawta-pink mt-6 mb-4">VENDOR CENTER</p>
          <h2 className="font-display text-4xl md:text-5xl font-light text-white tracking-tight leading-[1.05]">
            Showcase your brand<br /> at <em className="italic text-aawta-pink">Power to Drive Change.</em>
          </h2>
          <p className="text-white/70 mt-6 leading-relaxed">
            Limited vendor booths available for trucking, logistics, financial services,
            wellness, fashion, and lifestyle brands aligned with our audience.
          </p>
          <ul className="mt-8 space-y-2 text-sm text-white/60">
            <li>· Vendor guidelines emailed upon approval</li>
            <li>· Booth assignments confirmed 30 days before event</li>
            <li>· Load-in: Saturday, Dec 10, 12:00&ndash;4:00 PM</li>
          </ul>
        </div>

        <form onSubmit={submit} data-testid="vendor-form" className="md:col-span-7 glass-panel p-8 md:p-10 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="label-luxe">Business Name *</label>
              <input data-testid="ven-business" required className="input-luxe" value={form.business_name} onChange={handle("business_name")} />
            </div>
            <div>
              <label className="label-luxe">Contact Name *</label>
              <input data-testid="ven-contact" required className="input-luxe" value={form.contact_name} onChange={handle("contact_name")} />
            </div>
            <div>
              <label className="label-luxe">Email *</label>
              <input data-testid="ven-email" required type="email" className="input-luxe" value={form.email} onChange={handle("email")} />
            </div>
            <div>
              <label className="label-luxe">Phone</label>
              <input data-testid="ven-phone" className="input-luxe" value={form.phone} onChange={handle("phone")} />
            </div>
            <div>
              <label className="label-luxe">Product Category *</label>
              <input data-testid="ven-category" required placeholder="e.g. Logistics SaaS, Apparel, Insurance" className="input-luxe" value={form.product_category} onChange={handle("product_category")} />
            </div>
            <div>
              <label className="label-luxe">Website</label>
              <input data-testid="ven-website" className="input-luxe" value={form.website} onChange={handle("website")} />
            </div>
          </div>
          <div>
            <label className="label-luxe">Booth Preference</label>
            <select data-testid="ven-booth" className="input-luxe bg-aawta-jet" value={form.booth_preference} onChange={handle("booth_preference")}>
              <option value="">No preference</option>
              <option>Standard 6&apos; Booth</option>
              <option>Premium 10&apos; Booth</option>
              <option>Lounge / Activation Space</option>
            </select>
          </div>
          <div>
            <label className="label-luxe">Tell us about your business *</label>
            <textarea data-testid="ven-desc" required rows={4} className="input-luxe resize-none" value={form.description} onChange={handle("description")} />
          </div>

          {/* Optional company logo */}
          <div data-testid="ven-logo-field">
            <label className="label-luxe">Company Logo (Optional)</label>
            {form.logo_url ? (
              <div className="flex items-center gap-3 border border-white/15 bg-aawta-jet/60 p-3">
                <img
                  src={form.logo_url.startsWith("http") ? form.logo_url : `${API}${form.logo_url.replace(/^\/api/, "")}`}
                  alt="Uploaded logo preview"
                  className="w-14 h-14 object-contain bg-white/10 p-1"
                  data-testid="ven-logo-preview"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate" data-testid="ven-logo-filename">{form.logo_original_filename || "Logo uploaded"}</p>
                  <p className="text-overline text-aawta-pink mt-1">PENDING ADMIN APPROVAL · NOT PUBLIC</p>
                </div>
                <button type="button" onClick={removeLogo} data-testid="ven-logo-remove" className="text-white/60 hover:text-aawta-pink" aria-label="Remove logo">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="flex items-center gap-3 border border-dashed border-white/20 bg-aawta-jet/40 p-4 cursor-pointer hover:border-aawta-pink/50 transition-colors">
                {logoUploading ? (
                  <>
                    <Upload className="text-aawta-pink animate-pulse" size={18} />
                    <span className="text-sm text-white/70">Uploading…</span>
                  </>
                ) : (
                  <>
                    <ImageIcon className="text-aawta-pink" size={18} />
                    <div className="flex-1">
                      <p className="text-sm text-white">Upload your company logo</p>
                      <p className="text-xs text-white/40 mt-0.5">PNG, JPG, JPEG, or WebP · max 5 MB</p>
                    </div>
                    <span className="text-overline text-aawta-pink">BROWSE</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={uploadLogo}
                  disabled={logoUploading}
                  className="hidden"
                  data-testid="ven-logo-input"
                />
              </label>
            )}
            {logoError && (
              <p data-testid="ven-logo-error" className="text-xs text-red-400 mt-2">{logoError}</p>
            )}
            <p className="text-[11px] text-white/40 mt-2 leading-relaxed">
              Logos are stored securely and are not displayed publicly until an administrator
              approves the vendor and confirms promotional-use permission.
            </p>
          </div>

          <button type="submit" data-testid="ven-submit" disabled={submitting} className="btn-pink w-full disabled:opacity-50">
            {submitting ? "SUBMITTING..." : "SUBMIT VENDOR APPLICATION"} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </section>
  );
}

export function VolunteerForm() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", availability: [],
    skills: "", tshirt_size: "M", emergency_contact: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleAvail = (a) => {
    const has = form.availability.includes(a);
    setForm({
      ...form,
      availability: has ? form.availability.filter((x) => x !== a) : [...form.availability, a],
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/volunteers", form);
      toast.success("Welcome to the team. Your volunteer dashboard invite is on its way.");
      setForm({ full_name: "", email: "", phone: "", availability: [], skills: "", tshirt_size: "M", emergency_contact: "" });
    } catch {
      toast.error("Could not register volunteer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="volunteers"
      data-testid="volunteer-section"
      className="relative py-24 md:py-32 bg-aawta-charcoal scroll-mt-nav"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <HandHeart className="text-aawta-pink" size={28} />
          <p className="text-overline text-aawta-pink mt-6 mb-4">VOLUNTEER COMMAND CENTER</p>
          <h2 className="font-display text-4xl md:text-5xl font-light text-white tracking-tight leading-[1.05]">
            Be the force behind<br /> the <em className="italic text-aawta-pink">unforgettable night.</em>
          </h2>
          <p className="text-white/70 mt-6 leading-relaxed">
            Volunteers receive QR check-in, training videos, shift management, an emergency
            contact tree, and exclusive AAWTA merch. Mobile-friendly volunteer dashboard launches in November.
          </p>
        </div>

        <form onSubmit={submit} data-testid="volunteer-form" className="md:col-span-7 glass-panel p-8 md:p-10 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="label-luxe">Full Name *</label>
              <input data-testid="vol-name" required className="input-luxe" value={form.full_name} onChange={handle("full_name")} />
            </div>
            <div>
              <label className="label-luxe">Email *</label>
              <input data-testid="vol-email" required type="email" className="input-luxe" value={form.email} onChange={handle("email")} />
            </div>
            <div>
              <label className="label-luxe">Phone</label>
              <input data-testid="vol-phone" className="input-luxe" value={form.phone} onChange={handle("phone")} />
            </div>
            <div>
              <label className="label-luxe">T-Shirt Size</label>
              <select data-testid="vol-size" className="input-luxe bg-aawta-jet" value={form.tshirt_size} onChange={handle("tshirt_size")}>
                {["XS", "S", "M", "L", "XL", "XXL"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label-luxe">Availability *</label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {AVAIL_OPTIONS.map((a) => {
                const active = form.availability.includes(a);
                return (
                  <button
                    type="button"
                    key={a}
                    data-testid={`vol-avail-${a.split(" ")[0].toLowerCase()}`}
                    onClick={() => toggleAvail(a)}
                    className={`text-left p-3 text-xs border transition-all ${
                      active ? "border-aawta-pink bg-aawta-pink/10 text-white" : "border-white/15 text-white/70 hover:border-white/40"
                    }`}
                  >
                    {a}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="label-luxe">Skills / Interests</label>
            <input data-testid="vol-skills" placeholder="Logistics, hospitality, photography..." className="input-luxe" value={form.skills} onChange={handle("skills")} />
          </div>
          <div>
            <label className="label-luxe">Emergency Contact</label>
            <input data-testid="vol-emergency" placeholder="Name + phone" className="input-luxe" value={form.emergency_contact} onChange={handle("emergency_contact")} />
          </div>
          <button type="submit" data-testid="vol-submit" disabled={submitting} className="btn-pink w-full disabled:opacity-50">
            {submitting ? "REGISTERING..." : "JOIN THE VOLUNTEER TEAM"} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </section>
  );
}
